import { runtimeConfig } from "@src/runtime-config";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { getPathnameFromUrl, getQueryParamsFromSearch } from "@utils/url-utils";
import { useEffect, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- useAppParams is not available in this repo
import { useLocation, useParams } from "react-router-dom";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { QueryParams } from "@router/route-params";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useLanguage } from "@hooks/use-language";
import { StellarApiTypes } from "@stellar/stellar-api-client";

/**
 * No matter the dependencies it seems that the useEffect to call the init route
 * is called twice, so it stores similar to the isApiInitialized state, whether
 * initialized was already called.
 */
let isApiInitializedLocally = false;

/**
 * Hook to initialize the user in the dashboard 2.0 API
 */
export function useTrackPageLoad(): void {
  const [isApiInitialized, setIsApiInitialized] = useState<boolean>(false);
  const routeLocation = useLocation();
  const appParams = useParams();
  const { stellarApiClient } = useStellarApiClient();
  const { currentLanguage } = useLanguage();

  // Call the routes to initialize the user in the dashboard 2.0 API
  // This route will take care of tracking the page load in Amplitude for all users.
  useEffect(() => {
    async function trackPageLoaded(): Promise<void> {
      const language = currentLanguage.code;
      const urlPathname = getPathnameFromUrl(routeLocation.pathname, appParams);

      /**
       * Get the search string from the route location
       * Since the user module redirection uses hash routing, we need to extract the search from the hash instead.
       */
      const searchString: string = routeLocation.pathname.startsWith(
        "/user-module/"
      )
        ? routeLocation.hash.split("?")[1] || ""
        : routeLocation.search;
      const urlQuery = getQueryParamsFromSearch(searchString);

      const deviceSizes: StellarApiTypes.SubmitInitDeviceProps = {
        screenWidth: window.screen.width,
        windowWidth: window.innerWidth,
        screenHeight: window.screen.height,
        windowHeight: window.innerHeight,
        windowOrientation: window.screen.orientation?.type,
      };

      try {
        const appInitTime =
          LocalStorageUtils.getStringItem("app-init-time") ?? undefined;
        const search = new URLSearchParams(searchString);

        const response = await stellarApiClient.init.postInit({
          version: runtimeConfig.appVersion,
          application: "Sphere XG EntryPage",
          userId: "SPHERE_SXG_ENTRY_PAGE_USER",
          appInitTime,
          language,
          urlPathname,
          urlQuery,
          ulmClient: search.get(QueryParams.client) ?? undefined,
          ulmClientVersion: search.get(QueryParams.version) ?? undefined,
          ...deviceSizes,
        });
        if (response.success) {
          LocalStorageUtils.setStringItem(
            "app-init-time",
            Date.now().toString()
          );
        }
      } catch (error) {
        // Failing to call the init route should not block the user from using the app
        sentryCaptureError({
          title: "Error calling init route",
          error,
        });
      }
    }

    if (
      !isApiInitialized &&
      !isApiInitializedLocally &&
      routeLocation?.pathname &&
      appParams
    ) {
      isApiInitializedLocally = true;
      // Call this upfront to avoid multiple calls while the promise is being resolved
      setIsApiInitialized(true);
      void trackPageLoaded();
    }
  }, [
    appParams,
    isApiInitialized,
    stellarApiClient,
    routeLocation,
    currentLanguage.code,
  ]);
}
